import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { IconButton, Box, Slider } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

const useStyles = makeStyles((theme) => ({
    controlsContainer: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        minHeight: 58,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        background:
            "linear-gradient(180deg, rgba(8, 14, 10, 0.82) 0%, rgba(5, 9, 7, 0.96) 100%)",
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.16)",
        zIndex: 2,
        [theme.breakpoints.down("sm")]: {
            minHeight: 52,
            padding: "8px 10px",
            gap: 7,
        },
    },
    playButton: {
        color: "white !important",
        width: "36px !important",
        height: "36px !important",
        padding: "7px !important",
        borderRadius: "9px !important",
        border: "1px solid rgba(255, 255, 255, 0.15) !important",
        backgroundColor: "rgba(0, 149, 61, 0.9) !important",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.18)",
        transition:
            "background-color 140ms ease, transform 140ms ease !important",
        "&:hover": {
            backgroundColor: "rgb(0, 132, 54) !important",
            transform: "translateY(-1px)",
        },
    },
    muteButton: {
        color: "white !important",
        width: "36px !important",
        height: "36px !important",
        padding: "7px !important",
        borderRadius: "9px !important",
        border: "1px solid rgba(255, 255, 255, 0.12) !important",
        backgroundColor: "rgba(255, 255, 255, 0.08) !important",
        transition: "background-color 140ms ease !important",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.16) !important",
        },
    },
    timeDisplay: {
        minWidth: 94,
        padding: "4px 7px",
        color: "rgba(255, 255, 255, 0.9)",
        backgroundColor: "rgba(255, 255, 255, 0.07)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: 7,
        fontSize: 12,
        fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontVariantNumeric: "tabular-nums",
        textAlign: "center",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            minWidth: 82,
            padding: "3px 5px",
            fontSize: 11,
        },
    },
    progressSlider: {
        flex: 1,
        height: "4px !important",
        padding: "13px 0 !important",
        color: "rgb(0, 180, 74) !important",
        "& .MuiSlider-thumb": {
            width: "14px !important",
            height: "14px !important",
            backgroundColor: "rgb(0, 149, 61) !important",
            border: "2px solid white",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35) !important",
            transition:
                "transform 120ms ease, box-shadow 120ms ease !important",
            "&:hover, &.Mui-focusVisible": {
                boxShadow:
                    "0 0 0 5px rgba(0, 149, 61, 0.24), 0 2px 8px rgba(0, 0, 0, 0.35) !important",
            },
        },
        "& .MuiSlider-rail": {
            height: "4px !important",
            opacity: "1 !important",
            backgroundColor: "rgba(255, 255, 255, 0.24) !important",
            borderRadius: "4px !important",
        },
        "& .MuiSlider-track": {
            height: "4px !important",
            border: "none !important",
            backgroundColor: "rgb(0, 180, 74) !important",
            borderRadius: "4px !important",
            // MUI transitions the track width by default, while our thumb
            // position updates immediately. That makes the fill visibly trail
            // the thumb, especially when short clips advance quickly.
            transition: "none !important",
        },
    },
}));

function formatTime(seconds) {
    if (isNaN(seconds) || seconds < 0) return "0:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function VideoControls({
    isPlaying,
    isMuted,
    currentTime,
    duration,
    onPlayPause,
    onSeek,
    onMute,
    isAnnotation,
    bottomOffset = 0,
}) {
    const classes = useStyles();
    const [isSeeking, setIsSeeking] = useState(false);
    const [localProgress, setLocalProgress] = useState(0);

    // Update local progress when not seeking
    useEffect(() => {
        if (!isSeeking && duration > 0) {
            const progress = (currentTime / duration) * 100;
            setLocalProgress(Math.min(100, Math.max(0, progress)));
        }
    }, [currentTime, duration, isSeeking]);

    const handleSeekChange = (event, newValue) => {
        setIsSeeking(true);
        setLocalProgress(newValue);
    };

    const handleSeekCommit = (event, newValue) => {
        setIsSeeking(false);
        const seekTime = (newValue / 100) * duration;
        onSeek(seekTime);
    };

    return (
        <Box
            className={classes.controlsContainer}
            style={{ bottom: bottomOffset }}
        >
            <IconButton
                className={classes.playButton}
                onClick={onPlayPause}
                size="small"
            >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>

            {!isAnnotation && (
                <IconButton
                    className={classes.muteButton}
                    onClick={onMute}
                    size="small"
                >
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
            )}

            <span className={classes.timeDisplay}>
                {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <Slider
                className={classes.progressSlider}
                value={localProgress}
                onChange={handleSeekChange}
                onChangeCommitted={handleSeekCommit}
                min={0}
                max={100}
                step={0.01}
            />
        </Box>
    );
}
